import clsx from 'clsx'
import { ComponentPropsWithoutRef, forwardRef, ReactNode } from 'react'

export type ButtonGroupSize = 'sm' | 'md'

export interface ButtonGroupItem {
    id: string
    label?: ReactNode
    iconLeading?: ReactNode
    iconOnly?: boolean
    disabled?: boolean
}

export interface ButtonGroupProps extends Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> {
    items: ButtonGroupItem[]
    size?: ButtonGroupSize
    value?: string
    onChange?: (id: string) => void
}

const ITEM_SIZE_CLASSES: Record<ButtonGroupSize, string> = {
    sm: 'min-h-9 px-3.5 py-spacing-md',
    md: 'min-h-10 px-spacing-xl py-spacing-md',
}

const ICON_ONLY_SIZE_CLASSES: Record<ButtonGroupSize, string> = {
    sm: 'min-h-9 min-w-9 p-spacing-md',
    md: 'min-h-10 min-w-10 p-2.5',
}

export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
    ({ items, size = 'md', value, onChange, className, ...rest }, ref) => {
        const handleSelect = (item: ButtonGroupItem) => {
            if (item.disabled) {
                return
            }
            onChange?.(item.id)
        }

        return (
            <div
                ref={ref}
                role="group"
                className={clsx(
                    'inline-flex isolate',
                    'rounded-radius-md border border-solid border-border-primary shadow-xs-skeuomorphic',
                    className,
                )}
                {...rest}>
                {items.map((item) => {
                    const isCurrent = item.id === value
                    const iconOnly = item.iconOnly === true
                    const accessibleLabel =
                        iconOnly && typeof item.label === 'string' ? item.label : undefined

                    return (
                        <button
                            key={item.id}
                            type="button"
                            disabled={item.disabled}
                            aria-pressed={isCurrent}
                            aria-label={accessibleLabel}
                            title={accessibleLabel}
                            onClick={() => handleSelect(item)}
                            className={clsx(
                                'relative inline-flex items-center justify-center gap-spacing-sm',
                                'font-body text-sm font-semibold',
                                'border-0 border-r border-solid border-border-primary last:border-r-0',
                                'first:rounded-l-radius-md last:rounded-r-radius-md',
                                'transition-colors duration-100',
                                'focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-base-white',
                                'disabled:cursor-not-allowed',
                                iconOnly ? ICON_ONLY_SIZE_CLASSES[size] : ITEM_SIZE_CLASSES[size],
                                isCurrent
                                    ? 'bg-background-bg-primary_hover text-text-secondary_hover'
                                    : 'bg-background-bg-primary text-text-secondary enabled:hover:bg-background-bg-primary_hover enabled:hover:text-text-secondary_hover',
                            )}>
                            <span
                                className={clsx(
                                    'inline-flex items-center gap-spacing-sm',
                                    item.disabled && 'opacity-50',
                                )}>
                                {item.iconLeading && (
                                    <span
                                        aria-hidden="true"
                                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
                                        {item.iconLeading}
                                    </span>
                                )}
                                {iconOnly ? null : item.label}
                            </span>
                        </button>
                    )
                })}
            </div>
        )
    },
)

ButtonGroup.displayName = 'ButtonGroup'
